import { useState, useEffect, useRef, useMemo } from "react";
import { BusyLabel, HitLoadingBlock, HitRing } from "@/components/loading";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { compressImage, humanUploadError } from "@/lib/imageCompress";
import { makeCardImgFallback } from "@/lib/imageFallback";
import { variantsForCard } from "@/lib/gameConfig";
import { invalidateCatalogCache } from "@/lib/catalogCache";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ShippingForm } from "@/pages/SellerDashboard";
import { SearchResultsP2, versionOpts } from "@/components/publish/SearchResultsP2";
import { SellerNetCards } from "@/components/publish/SellerNetCards";
import { shipRange, hasShipRates } from "@/lib/sellerNet";
import { QueueSheet } from "@/components/publish/QueueSheet";
import { isHoloCard } from "@/lib/rarityTier";
import { conditionOptions, condShort, condDesc, isLotProduct } from "@/lib/conditions";
import { HitInlineLoader } from "@/components/loading/HitInlineLoader";
import { P1Start } from "@/components/publish/P1Start";
import { toast } from "sonner";
import { Search, CheckCircle2, Plus, X, Package, ArrowRight, Info, Truck, Pencil, ChevronDown } from "lucide-react";
import { sellerNetFor } from "@/lib/pricing";
import { MultiPhotoInput } from "@/components/MultiPhotoInput";
import { RegisterItemsPanel } from "@/components/RegisterItemsPanel";
import { useGame, useActiveGames, GAMES } from "@/lib/game";
import { GameBadge } from "@/components/GameBadge";
import { GameIcon } from "@/components/GameIcon";
import { useDialog } from "@/lib/dialog";
import { CardScanner } from "@/components/CardScanner";
import { ScanLine } from "lucide-react";

import { emptyDraft, itemError, isComplete as itemComplete, MIN_PHOTOS, MAX_ITEMS as MAX_TABS } from "@/lib/publishQueue";
import { usePublishQueue } from "@/hooks/publish/usePublishQueue";
import { useResultsKeyboard } from "@/hooks/publish/useResultsKeyboard";
import { useEnterKey } from "@/hooks/publish/useEnterKey";
import { usePhotoDropPaste } from "@/hooks/publish/usePhotoDropPaste";
import { useWideLayout } from "@/hooks/publish/useWideLayout";

const REC_PHOTOS = 4;

// ---- Panel informativo de tarifas de envío (Feb 2026).
// Muestra los DOS costos que el vendedor tiene configurados (misma provincia
// vs otras provincias) en la pantalla de publicar. Si está en modo `detailed`
// mostramos la provincia propia + la mediana del resto (el mismo criterio que
// usa `defaultShipCost` para heredar el fallback). El botón "Editar" abre el
// mismo `ShippingForm` que vive en SellerDashboard dentro de un Dialog, así
// el vendedor edita sin perder el borrador de la publicación actual.
function ShippingConfigPreview({ shippingConfig, sellerProvince, onEdit }) {
  if (shippingConfig === null || shippingConfig === undefined || !hasShipRates(shippingConfig)) {
    return (
      <div data-testid="publish-shipping-preview" className="mb-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-md flex items-start gap-3">
        <Truck size={16} className="text-amber-400 mt-0.5 flex-none" />
        <div className="flex-1 text-[12px] text-amber-200 leading-relaxed">
          <b>No tienes tarifas de envío configuradas.</b> El sistema no puede calcular el envío automáticamente al comprador. Configura tus tarifas para heredarlas en cada publicación.
        </div>
        <Button type="button" data-testid="publish-shipping-edit-btn" onClick={onEdit} size="sm" variant="outline" className="border-amber-500 text-amber-200 hover:bg-amber-500/20 flex-none">
          <Pencil size={12} className="mr-1.5" />Configurar
        </Button>
      </div>
    );
  }
  const cfg = shippingConfig;
  let sameLabel = null, sameValue = null, otherLabel = null, otherValue = null, extraNote = null;
  if (cfg.mode === "simple") {
    sameLabel = "Misma provincia";
    sameValue = Number(cfg.same_province_rate ?? 0).toFixed(2);
    otherLabel = "Otras provincias";
    otherValue = Number(cfg.other_province_rate ?? 0).toFixed(2);
  } else if (cfg.mode === "detailed") {
    const rateMap = cfg.per_province_rates || {};
    const nums = Object.values(rateMap).map(Number).filter(n => !isNaN(n));
    nums.sort((a, b) => a - b);
    const median = nums.length ? nums[Math.floor(nums.length / 2)].toFixed(2) : "—";
    // Tarifa exacta de la provincia del vendedor (si está seteada)
    const ownRate = sellerProvince && rateMap[sellerProvince] !== undefined ? Number(rateMap[sellerProvince]).toFixed(2) : null;
    sameLabel = sellerProvince ? `Tu provincia (${sellerProvince})` : "Tu provincia";
    sameValue = ownRate !== null ? ownRate : "—";
    otherLabel = "Mediana del resto";
    otherValue = median;
    extraNote = "Tienes tarifas por provincia. Mostramos tu provincia y la mediana del resto. Edita para ver el detalle completo.";
  } else if (cfg.mode === "meetup_only") {
    return (
      <div data-testid="publish-shipping-preview" className="mb-3 p-3 bg-purple-500/10 border border-purple-500/30 rounded-md flex items-start gap-3">
        <Truck size={16} className="text-purple-300 mt-0.5 flex-none" />
        <div className="flex-1 text-[12px] text-purple-200 leading-relaxed">
          <b>Modo:</b> Solo encuentro presencial. No cobrás envío — solo compradores de tu misma provincia pueden comprarte.
        </div>
        <Button type="button" data-testid="publish-shipping-edit-btn" onClick={onEdit} size="sm" variant="outline" className="border-purple-500 text-purple-200 hover:bg-purple-500/20 flex-none">
          <Pencil size={12} className="mr-1.5" />Editar
        </Button>
      </div>
    );
  }
  return (
    <div data-testid="publish-shipping-preview" className="mb-3 p-3 bg-[#0F0F0F] border border-[#262626] rounded-md">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex items-center gap-2">
          <Truck size={14} className="text-emerald-400" />
          <span className="text-[11px] uppercase tracking-wider text-neutral-400">Tus tarifas configuradas</span>
        </div>
        <Button type="button" data-testid="publish-shipping-edit-btn" onClick={onEdit} size="sm" variant="outline" className="h-6 px-2 border-[#404040] text-neutral-300 hover:bg-[#171717] text-[11px]">
          <Pencil size={11} className="mr-1" />Editar
        </Button>
      </div>
      <div className="space-y-1 text-[12px]">
        <div className="flex justify-between" data-testid="publish-shipping-same-province">
          <span className="text-neutral-400">{sameLabel}:</span>
          <span className="font-mono text-white">${sameValue}</span>
        </div>
        <div className="flex justify-between" data-testid="publish-shipping-other-province">
          <span className="text-neutral-400">{otherLabel}:</span>
          <span className="font-mono text-white">${otherValue}</span>
        </div>
      </div>
      {extraNote && <div className="mt-2 text-[10px] text-neutral-500">{extraNote}</div>}
      <div className="mt-2 text-[10px] text-neutral-500 border-t border-[#262626] pt-2">
        El sistema elige la tarifa según la provincia del comprador. El valor de abajo es el fallback para esta publicación.
      </div>
    </div>
  );
}

export default function PublishCard() {
  const { activeGame, setActiveGame } = useGame();
  const ACTIVE_GAMES = useActiveGames();
  const { confirm } = useDialog();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [searching, setSearching] = useState(false);
  // Feb 2026 v7 — paginación real ("Ver más"). Guardamos offset y total
  // para saber cuántos hay en total y si podemos pedir más páginas. El
  // término activo (lastQuery) sirve para que "Ver más" pague al MISMO
  // término aunque el usuario haya empezado a escribir otro nuevo.
  const [searchOffset, setSearchOffset] = useState(0);
  const [searchTotal, setSearchTotal] = useState(0);
  const [searchHasMore, setSearchHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [lastQuery, setLastQuery] = useState("");
  const SEARCH_PAGE_SIZE = 20;
  // (Feb 2026) Metadatos de resiliencia del buscador Pokémon: si TCGdex está
  // caído mostramos un banner honesto en vez del CTA "agregar como custom".
  const [searchMeta, setSearchMeta] = useState({
    tcgdex_unavailable: false, source: "tcgdex_live", stale: false, cached_at: null,
  });
  // Dos apartados top-level: "cargar" (vender algo aprobado) vs "registrar"
  // (pedirle al admin que agregue algo nuevo). Antes se mezclaban en una
  // sola pantalla y la gente se confundía entre "publicar" y "crear".
  const [mode, setMode] = useState("cargar");
  const [newCardName, setNewCardName] = useState("");
  const [drafts, setDrafts] = useState([emptyDraft()]);
  const [activeTab, setActiveTab] = useState(0);
  const queue = usePublishQueue({ drafts, setDrafts, game: activeGame });
  const { loading, progress, savedQueue, setSavedQueue } = queue;
  const wide = useWideLayout();
  const [payoutInfo, setPayoutInfo] = useState(undefined);
  const [shippingConfig, setShippingConfig] = useState(null);
  const [shippingModalOpen, setShippingModalOpen] = useState(false);
  const { user } = useAuth();
  const nav = useNavigate();

  // Refs used for auto-scroll UX between publish steps.
  const searchBlockRef = useRef(null);
  const formRef = useRef(null);       // Paso 2 · Datos + desglose (form principal)
  const photosRef = useRef(null);     // Paso 3 · Fotos reales
  const registerPanelRef = useRef(null); // Apartado "Registrar" (scroll target)

  const smoothScrollTo = (ref, block = "start") => {
    requestAnimationFrame(() => {
      setTimeout(() => {
        try {
          ref?.current?.scrollIntoView({ behavior: "smooth", block });
        } catch { /* scrollIntoView not critical */ }
      }, 50);
    });
  };
  const scrolledToPhotosRef = useRef(false);

  useEffect(() => {
    api.get("/seller/payout-info")
      .then((r) => setPayoutInfo(r.data || null))
      .catch(() => setPayoutInfo(null));
    api.get("/seller/shipping-config")
      .then((r) => setShippingConfig(r.data || null))
      .catch(() => setShippingConfig(null));
  }, []);

  // Compute a reasonable default ship_cost from the seller's shipping-config
  // (task 1: inherit shipping settings automatically per publication).
  const defaultShipCost = (() => {
    const cfg = shippingConfig;
    if (!cfg) return 5;
    if (cfg.mode === "meetup_only") return 0;
    if (cfg.mode === "simple") return Number(cfg.same_province_rate ?? 5);
    if (cfg.mode === "detailed") {
      // Median of provinces so a typical buyer pays something in the middle;
      // seller can override per-publication if he wants a fixed price.
      const rates = Object.values(cfg.per_province_rates || {}).map(Number).filter(n => !isNaN(n));
      if (!rates.length) return 5;
      rates.sort((a, b) => a - b);
      return rates[Math.floor(rates.length / 2)];
    }
    return 5;
  })();

  // Once the shippingConfig arrives, pre-fill any draft that hasn't been
  // manually edited by the seller so they don't have to retype it every time.
  useEffect(() => {
    if (shippingConfig === null) return;
    setDrafts((ds) => ds.map((d) => (
      d.form.ship_cost_from_config === true || (d.form.ship_cost === 5 && d.form.ship_cost_from_config === false)
        ? { ...d, form: { ...d.form, ship_cost: defaultShipCost, ship_cost_from_config: true } }
        : d
    )));
  }, [shippingConfig, defaultShipCost]);

  const cur = drafts[activeTab];
  const setCur = (updater) => setDrafts(ds => ds.map((d, i) => i === activeTab ? updater(d) : d));

  // Cambio de juego con guardia: si el vendedor tiene búsqueda, cartas
  // seleccionadas, fotos subidas o está en el apartado Registrar, avisamos
  // y pedimos confirmación antes de borrar todo su progreso local.
  const requestChangeGame = async (nextGame) => {
    if (nextGame === activeGame) return;
    const hasProgress =
      drafts.some((d) => d.selected || (d.photos && d.photos.length > 0)) ||
      results.length > 0 ||
      q.trim() !== "" ||
      mode === "registrar";
    if (hasProgress) {
      const ok = await confirm({
        title: `¿Cambiar a ${GAMES[nextGame]?.label || nextGame}?`,
        description: "Perderás el progreso que tienes en esta pantalla: búsqueda, cartas seleccionadas, fotos subidas y datos del formulario. Esta acción no se puede deshacer.",
        confirmText: "Sí, cambiar y borrar",
        cancelText: "Cancelar",
        destructive: true,
      });
      if (!ok) return;
    }
    // Reset del progreso local antes de cambiar de juego.
    setQ("");
    setResults([]);
    setSearched(false);
    setDrafts([emptyDraft()]);
    setActiveTab(0);
    scrolledToPhotosRef.current = false;
    setActiveGame(nextGame);
  };

  const [p1Type, setP1Type] = useState("card");
  const [registerTab, setRegisterTab] = useState("card");
  // P1: carta/sellado → buscador · lote/accesorio/producto nuevo → formulario de producto nuevo.
  const chooseType = (t) => {
    setP1Type(t === "sealed-new" ? "sealed" : t);
    if (t === "card" || t === "sealed") {
      setMode("cargar");
      smoothScrollTo(searchBlockRef);
      setTimeout(() => searchInputRef.current?.focus(), 300);
      return;
    }
    setRegisterTab(t === "lot" ? "bundle" : t === "accessory" ? "accessory" : "sealed");
    setMode("registrar");
    smoothScrollTo(registerPanelRef);
  };
  const searchInputRef = useRef(null);
  const searchAbortRef = useRef(null);
  const [enriching, setEnriching] = useState(false);

  // Lo de TCGdex llega después y se fusiona en las mismas filas (no se reordena nada).
  const enrichRows = async (rows, signal) => {
    const ids = rows.filter((r) => !r.enriched && !r.is_custom).map((r) => r.id);
    if (!ids.length) { setEnriching(false); return; }
    setEnriching(true);
    try {
      const { data } = await api.post("/tcgdex/enrich", { ids }, { signal });
      const upd = data?.rows || {};
      setResults((rs) => rs.map((r) => {
        const u = upd[r.id];
        if (!u) return r;
        const clean = Object.fromEntries(Object.entries(u).filter(([, v]) => v != null));
        return { ...r, ...clean, image_url: r.image_url || clean.image_url, enriched: true };
      }));
    } catch { /* cancelada o sin red: las filas quedan con lo que ya había */ }
    finally { if (!signal.aborted) setEnriching(false); }
  };

  const search = async (term) => {
    const qq = (typeof term === "string" ? term : q).trim();
    if (!qq) return;
    searchAbortRef.current?.abort();
    const ctrl = new AbortController();
    searchAbortRef.current = ctrl;
    setEnriching(false);
    setSearching(true);
    setSearched(false);
    setSearchOffset(0);
    setSearchTotal(0);
    setSearchHasMore(false);
    setLastQuery(qq);
    try {
      // Ruteamos a la API correcta según el juego activo:
      // - Pokémon → TCGdex (existente)
      // - Riftbound → RiftScribe (nuevo, fallback RiftCodex en backend)
      // - Yu-Gi-Oh → mirror local `/yugioh/search` (feb 2026 v30, Slice 3)
      const endpoint = activeGame === "riftbound" ? "/riftscribe/search"
        : activeGame === "yugioh" ? "/yugioh/search"
        : "/tcgdex/search";
      const params = activeGame === "riftbound"
        ? { q: qq, offset: 0, limit: SEARCH_PAGE_SIZE }
        : activeGame === "yugioh"
        ? { q: qq, offset: 0, limit: SEARCH_PAGE_SIZE }
        : { q: qq, lang: "both", offset: 0, limit: SEARCH_PAGE_SIZE, enrich: false };
      let shown = [];
      if (activeGame === "pokemon") {
        // 1) Lo que ya está en HitTCG, al instante.
        const loc = await api.get(endpoint, { params: { ...params, local: true }, signal: ctrl.signal }).catch(() => null);
        shown = loc?.data?.results || [];
        if (shown.length) { setResults(shown); setSearched(true); setSearching(false); setEnriching(true); }
      }
      const r = await api.get(endpoint, { params, signal: ctrl.signal });
      // Feb 2026 v7 — ambos endpoints devuelven ahora `{results, total,
      // offset, limit, has_more, ...}`. El campo `tcgdex_unavailable` solo
      // aplica a Pokémon; para Riftbound queda en false.
      const data = r.data || {};
      const full = Array.isArray(data.results) ? data.results : (Array.isArray(data) ? data : []);
      // 2) Lo nuevo de TCGdex se agrega DEBAJO, sin mover lo que ya está en pantalla.
      const byId = new Map(full.map((c) => [c.id, c]));
      const seen = new Set(shown.map((c) => c.id));
      const rows = [...shown.map((c) => ({ ...c, ...(byId.get(c.id) || {}), image_url: c.image_url || byId.get(c.id)?.image_url })),
        ...full.filter((c) => !seen.has(c.id))];
      setResults(rows);
      setSearchOffset(full.length);
      setSearchTotal(typeof data.total === "number" ? data.total : rows.length);
      setSearchHasMore(!!data.has_more);
      setSearchMeta({
        tcgdex_unavailable: !!data.tcgdex_unavailable,
        source: data.source || (activeGame === "riftbound" ? "riftscribe" : activeGame === "yugioh" ? "yugioh_mirror" : "tcgdex_live"),
        stale: !!data.stale,
        cached_at: data.cached_at || null,
      });
      setSearched(true);
      setSearching(false);
      if (activeGame === "pokemon" && rows.length) enrichRows(rows, ctrl.signal); else setEnriching(false);
    } catch (e) {
      if (ctrl.signal.aborted || e?.name === "CanceledError") return;
      toast.error("Error al buscar");
    } finally { if (searchAbortRef.current === ctrl) setSearching(false); }
  };

  // Debounce 300 ms mientras escribe; la búsqueda anterior se cancela.
  useEffect(() => {
    if (q.trim().length < 2) { return undefined; }
    const t = setTimeout(() => search(q), 300);
    return () => { clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, activeGame]);

  // Precarga de las búsquedas más comunes al abrir Publicar.
  useEffect(() => {
    api.post("/publish/prewarm", null, { params: { game: activeGame } }).catch(() => {});
  }, [activeGame]);

  // "Ver más": paga la siguiente página del MISMO término.
  const loadMoreResults = async () => {
    if (loadingMore || !searchHasMore || !lastQuery) return;
    setLoadingMore(true);
    try {
      const endpoint = activeGame === "riftbound" ? "/riftscribe/search"
        : activeGame === "yugioh" ? "/yugioh/search"
        : "/tcgdex/search";
      const params = activeGame === "riftbound"
        ? { q: lastQuery, offset: searchOffset, limit: SEARCH_PAGE_SIZE }
        : activeGame === "yugioh"
        ? { q: lastQuery, offset: searchOffset, limit: SEARCH_PAGE_SIZE }
        : { q: lastQuery, lang: "both", offset: searchOffset, limit: SEARCH_PAGE_SIZE };
      const r = await api.get(endpoint, { params });
      const data = r.data || {};
      const rows = Array.isArray(data.results) ? data.results : [];
      // Dedup defensivo por id — el mismo bug del catálogo (pagina 2 con
      // items ya en pagina 1) no debe repetirse aquí.
      const seen = new Set(results.map((c) => c.id));
      const merged = [...results];
      for (const c of rows) {
        if (c.id && !seen.has(c.id)) { seen.add(c.id); merged.push(c); }
      }
      setResults(merged);
      setSearchOffset(searchOffset + rows.length);
      setSearchHasMore(!!data.has_more);
    } catch { toast.error("Error al cargar más resultados"); } finally { setLoadingMore(false); }
  };

  const loadReference = async (cardId, lang, variant) => {
    try {
      const r = await api.get(`/catalog/variant/${cardId}/${lang}`);
      setCur(d => ({ ...d, reference: r.data.reference }));
    } catch { setCur(d => ({ ...d, reference: null })); }
  };

  const pickCard = (card, opts = {}) => {
    // Auto-set variant + condition for sealed products so publishing works with hidden fields.
    const isSealed = card?.is_sealed || (card?.product_type && card.product_type !== "card");
    // Feb 2026 v30 Slice 3 — Yu-Gi-Oh: si la carta tiene UNA sola impresión en
    // `all_printings`, la preseleccionamos (ergonomía). Si tiene varias, el
    // vendedor elige. Pokémon/Riftbound: campos quedan vacíos y no se envían.
    const printings = Array.isArray(card?.all_printings) ? card.all_printings : [];
    const soloPrint = opts.printing || ((activeGame === "yugioh" && printings.length === 1) ? printings[0] : null);
    setCur(d => ({
      ...d,
      selected: (!card.variants_available && card.versions?.length)
        ? { ...card, holo_card: card.holo_card || card.versions.some((v) => v.label === "Holo"),
            variants_available: Object.fromEntries(card.versions.map((v) => [v.key, true])) } : card,
      form: {
        ...d.form,
        variant: isSealed ? "sealed" : (opts.variant || d.form.variant || "normal"),
        version_label: opts.label || "",
        condition: isSealed ? "SEALED" : (d.form.condition || "NM"),
        language: isSealed && d.form.language === "otro" ? "na" : d.form.language,
        yugioh_rarity: soloPrint ? (soloPrint.rarity || "") : "",
        yugioh_set_code: soloPrint ? (soloPrint.set_code || "") : "",
        first_edition: !!opts.first_edition,
        ...(opts.draft?.form || {}),
      },
      ...(opts.draft?.photos ? { photos: opts.draft.photos } : {}),
    }));
    loadReference(card.id, cur.form.language, cur.form.variant);
    // Reseteamos el guard para que auto-scroll a fotos vuelva a funcionar
    // en esta nueva selección.
    scrolledToPhotosRef.current = false;
    // Guiamos visualmente al Paso 2 (Datos) para que el vendedor entienda
    // que debe continuar llenando el formulario.
    smoothScrollTo(formRef);
  };

  const createCustom = null; // moved to CustomCardForm component

  const addTab = () => {
    if (drafts.length >= MAX_TABS) { toast.error(`Máximo ${MAX_TABS} cartas por sesión`); return; }
    setDrafts(ds => [...ds, emptyDraft()]);
    setActiveTab(drafts.length);
  };

  const removeTab = (idx) => {
    if (drafts.length === 1) return;
    const newDrafts = drafts.filter((_, i) => i !== idx);
    setDrafts(newDrafts);
    if (activeTab >= newDrafts.length) setActiveTab(newDrafts.length - 1);
  };

  const isComplete = (d) => itemComplete(d, activeGame);

  // P4 · añade la carta a la cola, vacía el buscador y mantiene idioma, condición y envío.
  const addToQueue = () => {
    if (!cur.selected) { toast.error("Elige una carta primero"); return; }
    if (!(parseFloat(cur.form.price) >= 0.25)) { toast.error("Pon un precio (mínimo $0.25)"); return; }
    if (drafts.length >= MAX_TABS) { toast.error(`Máximo ${MAX_TABS} cartas por sesión`); return; }
    const base = emptyDraft();
    const { language, condition, ship_cost, ship_cost_from_config } = cur.form;
    const next = { ...base, form: { ...base.form, language, condition: condition === "SEALED" ? "NM" : condition, ship_cost, ship_cost_from_config } };
    const prev = activeTab;
    if (savedQueue?.length) { restoreQueueInto(next); return; }
    const n = drafts.filter((d) => d.selected).length;
    setDrafts((ds) => [...ds, next]);
    uploadInBackground(cur);
    setActiveTab(drafts.length);
    setQ(""); setResults([]); setSearched(false);
    smoothScrollTo(searchBlockRef);
    setTimeout(() => searchInputRef.current?.focus(), 350);
    toast(`${n} en la cola · busca la siguiente`, {
      position: "bottom-center",
      action: { label: "Deshacer", onClick: () => { setDrafts((ds) => ds.filter((d) => d.id !== next.id)); setActiveTab(prev); } },
    });
  };

  // Fotos de la cola: se suben en segundo plano y quedan como rutas (sobreviven recargas).
  const uploadInBackground = (d) => queue.uploadInBackground(d, (n) => toast.error(`${n} foto(s) no se subieron · vuelve a agregarlas`));

  // Si arma una cola nueva con el aviso abierto, se juntan las dos (nada se pierde).
  const restoreQueueInto = (next) => {
    const items = queue.savedAsDrafts();
    uploadInBackground(cur);
    setDrafts((ds) => [...items, ...ds, next]);
    setActiveTab(items.length + drafts.length);
    setSavedQueue([]);
    setQ(""); setResults([]); setSearched(false);
    toast(`${items.length + drafts.filter((d) => d.selected).length} en la cola · busca la siguiente`, { position: "bottom-center" });
  };
  const restoreQueue = () => {
    const items = queue.savedAsDrafts();
    setDrafts([...items, emptyDraft()]);
    setActiveTab(items.length);
    setSavedQueue([]);
  };
  const openQueueItem = (i, where) => {
    setActiveTab(i);
    smoothScrollTo(where === "photos" ? photosRef : formRef);
  };

  const submitAll = async (e, onlyComplete = false, only = null) => {
    e?.preventDefault?.();
    if (loading || queue.locked.current) return;
    const list = only || (onlyComplete ? drafts.filter(isComplete) : drafts.filter((d) => d.selected));
    if (!list.length) { toast.error("Selecciona una carta o producto sellado"); return; }
    for (let i = 0; i < list.length; i++) {
      const msg = itemError(list[i], activeGame);
      if (msg) { toast.error(`Carta ${i + 1}: ${msg}`); setActiveTab(drafts.indexOf(list[i])); return; }
    }
    const res = await queue.publish(list);
    if (!res) return;
    setActiveTab(0);
    if (res.failed.length) {
      toast[res.ok > 0 ? "warning" : "error"](res.text, { duration: 8000 });
      return;
    }
    toast.success(res.text);
    const leftover = drafts.filter((d) => d.selected && !list.includes(d));
    if (leftover.length) { setDrafts(leftover); return; }
    setDrafts([emptyDraft()]);
    await queue.clearServer();
    nav("/seller");
  };

  // D1 · teclado y fotos por arrastre / Ctrl+V.
  const pickFromKeyboard = (c, v) => { const o = v ? versionOpts(activeGame, v) : {}; pickCard(c, o); };
  const kb = useResultsKeyboard({ results, enabled: mode === "cargar" && wide, onPick: pickFromKeyboard, searchRef: searchInputRef, resetKey: q });
  useEnterKey(formRef, () => { if (cur.selected && wide) addToQueue(); }, wide && !!cur.selected);
  const photoDrop = usePhotoDropPaste((files) => setCur((d) => ({ ...d, photos: [...d.photos, ...files].slice(0, 12) })), mode === "cargar" && !!cur.selected);

  const netPrice = parseFloat(cur.form.price) || 0;
  const [tiers, setTiers] = useState([]);
  useEffect(() => {
    api.get("/config/public").then((r) => setTiers(r.data?.commission_tiers || [])).catch(() => {});
  }, []);
  const activeTier = (() => {
    if (!netPrice || !tiers.length) return null;
    for (let i = 0; i < tiers.length; i++) {
      const t = tiers[i];
      if (t.max === null || t.max === undefined || netPrice <= t.max) {
        const prev = i === 0 ? 0 : tiers[i - 1].max;
        return { ...t, prev, index: i };
      }
    }
    return null;
  })();
  // Model B: buyer pays exactly the seller's listed price. The seller absorbs
  // both the HitTCG tier commission and Payphone's 5.75% processing fee.
  // Feb 2026: la comisión Payphone es CONDICIONAL al método de pago que
  // elija el comprador — con tarjeta se descuenta, con transferencia no.
  // Mostramos las dos cifras para que la estimación sea honesta.
  // ¿Se cobró el % del tramo o el piso min_fee del tramo? (para etiqueta honesta)
  const hittcgRawFee = activeTier ? netPrice * activeTier.pct / 100 : netPrice * 0.10;
  const hittcgMinFee = activeTier?.min_fee ?? 0;
  const minFeeApplied = activeTier ? hittcgRawFee < hittcgMinFee : false;
  const hittcgFee = activeTier ? Math.max(hittcgRawFee, hittcgMinFee) : hittcgRawFee;
  const payphoneFee = netPrice * 0.0575;
  const sellerReceivesCard = Math.max(0, netPrice - hittcgFee - payphoneFee);      // con tarjeta
  const sellerReceivesTransfer = Math.max(0, netPrice - hittcgFee);                // con transferencia
  // Piso del pedido (una sola vez, no por carta) — se lee del config, nunca hardcoded.
  const orderMinFee = tiers?.[0]?.min_fee;

  if (payoutInfo === undefined) {
    return <div className="max-w-4xl mx-auto px-5 py-8 min-h-[70vh]"><HitLoadingBlock section="inventory" /></div>;
  }

  if (payoutInfo === null) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="font-display text-2xl sm:text-3xl md:text-4xl mb-4">PUBLICAR CARTAS</h1>
        <div className="bg-[#111] border border-amber-500/40 rounded-md p-6" data-testid="publish-payout-block">
          <div className="flex items-start gap-3 mb-4">
            <div className="text-amber-400 text-3xl leading-none">⚠</div>
            <div className="flex-1">
              <h2 className="font-display text-xl mb-1">Agrega tus datos de cobro para publicar</h2>
              <p className="text-sm text-neutral-400">
                Para publicar cartas necesitamos saber cómo transferirte tus ganancias tras cada venta cerrada. Ve a <b className="text-white">Mis Ventas → Datos de Cobro</b> y registra tu cuenta bancaria. La cédula del titular <b className="text-white">debe coincidir con tu cédula verificada</b>.
              </p>
            </div>
          </div>
          <Button data-testid="publish-goto-payout" onClick={() => nav("/seller?tab=payout")} className="bg-[#EF4444] hover:bg-[#DC2626]">
            Agregar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
    <div className={`${wide && mode === "cargar" ? "max-w-[1440px]" : "max-w-4xl"} mx-auto px-4 py-8`}>
      <h1 className="font-display text-2xl sm:text-3xl md:text-4xl mb-2">PUBLICAR / VENDER</h1>
      <p className="text-sm text-neutral-500 mb-5">¿Qué vas a vender? Elige y sigue los pasos.</p>

      {/* Pestañas de juego — mismo estilo que catálogo, delgadas.
          Al cambiar de pestaña se pide confirmación si hay progreso local
          para no borrar por accidente cartas seleccionadas o fotos subidas. */}
      <div className="flex items-center gap-1 mb-4 border-b border-[#262626] overflow-x-auto" data-testid="publish-game-tabs" role="tablist">
        {ACTIVE_GAMES.map((g) => (
          <button
            key={g.key}
            type="button"
            role="tab"
            aria-selected={activeGame === g.key}
            data-testid={`publish-game-tab-${g.key}`}
            onClick={() => requestChangeGame(g.key)}
            className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap transition-colors border-b-2 ${activeGame === g.key ? "text-white" : "border-transparent text-neutral-500 hover:text-white"}`}
            style={activeGame === g.key ? { borderColor: g.color, color: "#fff" } : undefined}
          >
            <GameIcon game={g.key} size="sm" />
            <span>{g.label}</span>
          </button>
        ))}
      </div>
      {!cur.selected && (
        <P1Start active={p1Type} onType={chooseType}
          onAgain={(p) => { setMode("cargar"); pickCard(p.card, { variant: p.variant, draft: { form: { price: String(p.price), condition: p.condition, language: p.language, quantity: 1 } } }); }} />
      )}
      <div className="grid grid-cols-2 gap-2 sm:gap-3 mb-6" data-testid="publish-mode-tabs">
        <button
          type="button"
          data-testid="publish-mode-cargar"
          onClick={() => { setMode("cargar"); smoothScrollTo(searchBlockRef); }}
          className={`p-3 sm:p-4 rounded-md border-2 text-left transition-colors min-w-0 ${mode === "cargar" ? "border-[#EF4444] bg-[#EF4444]/10" : "border-[#262626] bg-[#111] hover:border-neutral-600"}`}
        >
          <div className="font-display text-sm sm:text-lg md:text-xl text-white break-words leading-tight">📤 CARGAR</div>
          <div className="text-[10px] sm:text-[11px] text-neutral-500 mt-1 break-words leading-tight">Producto, carta, base, deck y más que ya están en el catálogo</div>
        </button>
        <button
          type="button"
          data-testid="publish-mode-registrar"
          onClick={() => { setMode("registrar"); smoothScrollTo(registerPanelRef); }}
          className={`p-3 sm:p-4 rounded-md border-2 text-left transition-colors min-w-0 ${mode === "registrar" ? "border-amber-500 bg-amber-500/10" : "border-[#262626] bg-[#111] hover:border-neutral-600"}`}
        >
          <div className="font-display text-sm sm:text-lg md:text-xl text-white break-words leading-tight">📝 REGISTRO</div>
          <div className="text-[10px] sm:text-[11px] text-neutral-500 mt-1 break-words leading-tight">Registra un producto que no aparece en el buscador de carga</div>
        </button>
      </div>

      {mode === "registrar" && (
        <div ref={registerPanelRef}>
          <RegisterItemsPanel key={registerTab} initialSubTab={registerTab} initialName={newCardName} onNavigateToCargar={() => { setMode("cargar"); smoothScrollTo(searchBlockRef); }}
            onPublishExisting={(card, draft) => { setMode("cargar"); pickCard(card, { draft }); }}
            shippingConfig={shippingConfig} onConfigureShip={() => setShippingModalOpen(true)} />
        </div>
      )}

      {mode === "cargar" && (
        /* Indicador de pasos numerados para el flujo Cargar:
            1) Busca 2) Datos+desglose 3) Fotos y publica. */
        <div className="mb-5 flex flex-wrap items-center gap-1.5 sm:gap-3 text-[10px] sm:text-[11px] uppercase tracking-wider" data-testid="publish-steps">
          <span className={`px-2 py-1 rounded whitespace-nowrap ${cur.selected ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" : "bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30"}`}>
            1 · Busca
          </span>
          <span className="text-neutral-700">→</span>
          <span className={`px-2 py-1 rounded whitespace-nowrap ${cur.selected ? (cur.photos.length ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/30" : "bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30") : "bg-[#111] text-neutral-600 border border-[#262626]"}`}>
            2 · Datos
          </span>
          <span className="text-neutral-700">→</span>
          <span className={`px-2 py-1 rounded whitespace-nowrap ${cur.selected && cur.photos.length ? "bg-[#EF4444]/10 text-[#EF4444] border border-[#EF4444]/30" : "bg-[#111] text-neutral-600 border border-[#262626]"}`}>
            3 · Fotos y publica
          </span>
        </div>
      )}

      <div className={wide && mode === "cargar" ? "grid grid-cols-[1fr_1.1fr_340px] gap-6 items-start" : ""} data-testid="publish-d1-grid">
      {mode === "cargar" && (
      <div ref={searchBlockRef} className={`bg-[#111] border border-[#262626] rounded-md p-6 mb-6 ${wide ? "col-start-1 row-start-1 row-span-4 max-h-[calc(100vh-7rem)] overflow-y-auto sticky top-24" : ""}`}>
        <div className="text-neutral-500 text-xs uppercase tracking-wider mb-3">Paso 1 · Busca en el catálogo (nombre, número o set — ES + EN)</div>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" size={16} />
            <Input
              ref={searchInputRef}
              data-testid="publish-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), search())}
              placeholder={activeGame === "yugioh"
                ? 'Busca por nombre en inglés (ej. "dark magician", "blue-eyes")'
                : activeGame === "riftbound"
                ? 'Nombre o código (ej. "vi", "jhin", "piltover")'
                : 'Nombre, código (ej. "89/121", "base5-4") o Pikachu 89'}
              className="pl-9 h-[60px] text-base bg-[#171717] border-[#262626] rounded-[14px]"
              autoFocus
            />
          </div>
          <Button data-testid="publish-search-btn" type="button" onClick={search} disabled={searching} className="bg-[#EF4444] hover:bg-[#DC2626]">
            {searching ? "Buscando…" : "Buscar"}
          </Button>
          <Button
            data-testid="publish-scan-btn"
            type="button"
            variant="outline"
            title="Escanear carta con la cámara"
            onClick={() => setScannerOpen(true)}
            className="bg-[#171717] border-[#262626] hover:border-emerald-500 hover:bg-emerald-500/10 px-3"
          >
            <ScanLine size={16} className="text-emerald-400" />
            <span className="hidden sm:inline ml-1">Escanear</span>
          </Button>
        </div>
        <div className="mt-2 text-[11px] text-neutral-500" data-testid="p1-examples">Ej.: <span className="text-neutral-300">Pikachu</span> · <span className="text-neutral-300">089/198</span> · <span className="text-neutral-300">Obsidian Flames</span></div>
        {p1Type === "sealed" && (
          <button type="button" onClick={() => chooseType("sealed-new")} className="mt-2 text-xs text-purple-300 underline" data-testid="p1-sealed-new">¿No está? Producto nuevo</button>
        )}
        {results.length === 0 && searching && <HitInlineLoader section="search" className="mt-4" />}
        {results.length > 0 && (
          <SearchResultsP2 results={results} total={searchTotal} hasMore={searchHasMore} loadingMore={loadingMore}
            onMore={loadMoreResults} onPick={pickCard} selectedId={cur.selected?.id} game={activeGame} enriching={enriching}
            kbIndex={kb.activeIndex} kbVersion={kb.activeVersion}
            onNewCard={() => { setNewCardName(q); setMode("registrar"); }} />
        )}
        {/* Banner de degradación honesto — Feb 2026: NUNCA mentir con "no existe" cuando
            TCGdex está caído. `stale`/`local_fallback` muestran resultados parciales con aviso. */}
        {searched && searchMeta.stale && results.length > 0 && (
          <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/40 rounded-md text-xs text-amber-100" data-testid="publish-search-stale">
            <b>Resultados del caché.</b> El catálogo Pokémon está lento; los datos más recientes pueden tardar.
          </div>
        )}
        {searched && searchMeta.source === "local_fallback" && results.length > 0 && (
          <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/40 rounded-md text-xs text-amber-100" data-testid="publish-search-local-fallback">
            <b>Mostrando solo cartas ya publicadas en HitTCG.</b> El catálogo Pokémon está lento; vuelve a buscar en unos segundos para ver el catálogo completo.
          </div>
        )}
        {searched && results.length === 0 && searchMeta.source === "confirmed_not_found" && (
          <div className="mt-4 p-4 bg-blue-500/10 border border-blue-500/40 rounded-md flex items-start gap-3" data-testid="publish-confirmed-not-found">
            <Info size={18} className="text-blue-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm text-blue-100">
                <b>Confirmamos con el catálogo oficial: esa carta no existe.</b> Revisa el nombre o el número, o regístrala como personalizada.
              </div>
              <Button
                type="button"
                size="sm"
                onClick={() => { setMode("registrar"); smoothScrollTo(registerPanelRef); }}
                data-testid="publish-confirmed-goto-registrar"
                className="mt-3 bg-blue-600 hover:bg-blue-700"
              >
                Ir a Registrar producto/carta <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          </div>
        )}
        {searched && results.length === 0 && searchMeta.tcgdex_unavailable && searchMeta.source !== "confirmed_not_found" && (
          <div className="mt-4 p-4 bg-red-500/10 border border-red-500/40 rounded-md flex items-start gap-3" data-testid="publish-tcgdex-unavailable">
            <Info size={18} className="text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm text-red-100">
                <b>El catálogo de Pokémon no responde ahora mismo.</b> Es una fuente externa (TCGdex), no depende de HitTCG. Vuelve a intentar en unos minutos.
              </div>
              <Button
                type="button"
                size="sm"
                onClick={() => search()}
                data-testid="publish-search-retry"
                className="mt-3 bg-red-600 hover:bg-red-700"
              >
                Reintentar
              </Button>
            </div>
          </div>
        )}
        {searched && results.length === 0 && !searchMeta.tcgdex_unavailable && searchMeta.source !== "confirmed_not_found" && (
          <div className="mt-4 p-4 bg-amber-500/10 border border-amber-500/40 rounded-md flex items-start gap-3" data-testid="publish-no-results">
            <Info size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="text-sm text-amber-100">
                <b>No encontramos «{q}» en el catálogo.</b> Regístralo primero para que el admin lo apruebe. Cuando esté aprobado aparecerá aquí y podrás cargarlo para venta.
              </div>
              <Button
                type="button"
                size="sm"
                onClick={() => { setMode("registrar"); smoothScrollTo(registerPanelRef); }}
                data-testid="publish-goto-registrar"
                className="mt-3 bg-amber-600 hover:bg-amber-700"
              >
                Ir a Registrar producto/carta <ArrowRight size={14} className="ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
      )}

      {mode === "cargar" && savedQueue?.length > 0 && (
        <div className={`mb-4 p-3 rounded-[14px] border border-[#FF4438]/50 bg-[#FF4438]/5 flex items-center justify-between gap-3 ${wide ? "col-start-2" : ""}`} data-testid="queue-restore">
          <span className="text-sm text-white">Tienes {savedQueue.length} {savedQueue.length === 1 ? "carta" : "cartas"} en la cola</span>
          <div className="flex gap-2">
            <button type="button" onClick={() => setSavedQueue([])} className="text-xs text-neutral-400 underline" data-testid="queue-restore-discard">Descartar</button>
            <button type="button" onClick={restoreQueue} className="h-9 px-4 rounded-full bg-[#FF4438] text-white text-sm font-bold" data-testid="queue-restore-continue">Continuar</button>
          </div>
        </div>
      )}

      {/* Tabs for multiple drafts — solo visibles en Cargar */}
      {mode === "cargar" && (
      <div className={`flex items-center gap-1 mb-4 border-b border-[#262626] overflow-x-auto ${wide ? "col-start-2" : ""}`}>
        {drafts.map((d, i) => (
          <div key={d.id} className="flex items-center">
            <button
              type="button"
              onClick={() => setActiveTab(i)}
              data-testid={`publish-tab-${i}`}
              className={`px-4 py-2 text-sm ${i === activeTab ? "border-b-2 border-[#EF4444] text-white" : "text-neutral-500 hover:text-white"}`}
            >
              Carta {i + 1}{d.selected && " ✓"}{d.selected && (d.selected.is_sealed || (d.selected.product_type && d.selected.product_type !== "card")) && <span className="text-purple-300 ml-1">📦</span>}
            </button>
            {drafts.length > 1 && (
              <button type="button" onClick={() => removeTab(i)} data-testid={`publish-tab-close-${i}`} className="text-neutral-600 hover:text-[#EF4444] px-1"><X size={12} /></button>
            )}
          </div>
        ))}
        {drafts.length < MAX_TABS && (
          <button type="button" onClick={addTab} data-testid="publish-tab-add" className="ml-2 px-3 py-2 text-sm text-[#EF4444] hover:bg-[#EF4444]/10 rounded">
            <Plus size={14} className="inline mr-1" />Otra carta
          </button>
        )}
      </div>
      )}

      {mode === "cargar" && (
      <form ref={formRef} onSubmit={submitAll} className={`bg-[#111] border border-[#262626] rounded-md p-6 space-y-5 ${wide ? "col-start-2" : ""}`}>
        {cur.selected ? (
          <div className="flex items-center gap-3 p-3 bg-[#0A0A0A] border border-[#262626] rounded-md" data-testid="publish-selected">
            <CheckCircle2 size={20} className="text-[#EF4444]" />
            {cur.selected.image_url && <img src={cur.selected.image_url.startsWith("http")
              ? cur.selected.image_url
              : cur.selected.image_url.startsWith("/api/")
              ? `${process.env.REACT_APP_BACKEND_URL}${cur.selected.image_url}`
              : `${process.env.REACT_APP_BACKEND_URL}/api/files/public/${cur.selected.image_url}`} alt="" className="w-16 h-20 object-contain" />}
            <div className="flex-1 min-w-0">
              <div className="font-medium flex items-center gap-2 flex-wrap">{cur.selected.name}
                {cur.form.version_label && <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border border-[#FF4438] text-[#FF6B5E]" data-testid="publish-selected-version">{cur.form.version_label}</span>}
              </div>
              {cur.selected.set_name && (
                <div className="text-xs text-neutral-300 mt-0.5">
                  <span className="text-neutral-500">Set:</span> {cur.selected.set_name}
                  {cur.selected.serie_name ? <span className="text-neutral-500"> · {cur.selected.serie_name}</span> : null}
                </div>
              )}
              <div className="text-xs text-neutral-500 mt-0.5 font-mono">
                {cur.selected.local_id ? `#${cur.selected.local_id}` : ""}{cur.selected.local_id && cur.selected.set_id ? " · " : ""}{cur.selected.set_id || (cur.selected.local_id || cur.selected.set_name ? "" : cur.selected.id)}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-sm text-neutral-500">Selecciona una carta o producto sellado arriba para completar los datos de la pestaña {activeTab + 1}.</div>
        )}

        {cur.selected && (
          <>
            {cur.selected.is_sealed || (cur.selected.product_type && cur.selected.product_type !== "card") ? (
              <div className="text-[11px] text-purple-300 bg-purple-500/10 border border-purple-500/30 rounded p-2 -mb-2">
                <Package size={12} className="inline mr-1" /><b>Producto sellado</b> — no lleva condición (va sellado de fábrica). Solo elige idioma, precio y cantidad.
              </div>
            ) : activeGame === "yugioh" ? (
              /* Feb 2026 v30 Slice 3 — Yu-Gi-Oh: elección obligatoria de
                 rareza + set exactos. Las opciones salen de
                 `cur.selected.all_printings` (lista REAL de printings de esa
                 carta — Blue-Eyes tiene 78, Dark Magician 59, etc.). Si no
                 trae printings (edge case) ofrecemos lista plana + set manual
                 para no bloquear el publish. */
              (() => {
                const printings = Array.isArray(cur.selected.all_printings) ? cur.selected.all_printings : [];
                const hasPrintings = printings.length > 0;
                const curKey = `${cur.form.yugioh_rarity}|${cur.form.yugioh_set_code}`;
                return (
                  <div className="grid grid-cols-1 gap-2 -mb-2 p-3 bg-purple-500/5 border border-purple-500/30 rounded" data-testid="publish-yugioh-printing-block">
                    <Label className="text-neutral-300 text-xs uppercase tracking-wider">Rareza + Set exactos <span className="text-purple-300">(obligatorio)</span></Label>
                    {hasPrintings ? (
                      <>
                        {/* Feb 2026 v30.4 — Combobox buscable (cmdk) en vez del
                            Select plano. Dark Magician tiene 59 printings; sin
                            búsqueda el vendedor scrollea para siempre. El filtro
                            es acento-tolerante, case-insensitive y parcial;
                            matchea rareza + set_code + set_name a la vez
                            (cmdk filtra por el prop `value` de cada CommandItem). */}
                        <YugiohPrintingCombobox
                          printings={printings}
                          value={curKey !== "|" ? curKey : ""}
                          onChange={(key) => {
                            const [r, sc] = (key || "|").split("|");
                            setCur(d => ({ ...d, form: { ...d.form, yugioh_rarity: r, yugioh_set_code: sc } }));
                          }}
                        />
                        <div className="text-[10px] text-neutral-500">
                          Cada impresión es un producto distinto en el mercado — mismo nombre, precio muy diferente según rareza+set.
                        </div>
                      </>
                    ) : (
                      /* Fallback edge case: carta sin printings en el mirror.
                         Permitimos input manual para no bloquear. */
                      <div className="space-y-2">
                        <div className="text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2">
                          Esta carta no trae lista de impresiones del espejo. Rellena rareza y código de set a mano.
                        </div>
                        <Select
                          value={cur.form.yugioh_rarity || undefined}
                          onValueChange={(v) => setCur(d => ({ ...d, form: { ...d.form, yugioh_rarity: v } }))}
                        >
                          <SelectTrigger data-testid="publish-yugioh-rarity-fallback" className="bg-[#171717] border-[#262626]">
                            <SelectValue placeholder="Rareza" />
                          </SelectTrigger>
                          <SelectContent className="bg-[#171717] border-[#262626] text-white">
                            {["Common","Rare","Super Rare","Ultra Rare","Ultimate Rare","Secret Rare","Ghost Rare","Starlight Rare","Quarter Century Secret Rare","Prismatic Secret Rare"].map(r => (
                              <SelectItem key={r} value={r}>{r}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          data-testid="publish-yugioh-setcode-fallback"
                          placeholder='Código de set (ej. "LOB-001")'
                          value={cur.form.yugioh_set_code}
                          onChange={(e) => setCur(d => ({ ...d, form: { ...d.form, yugioh_set_code: e.target.value.trim() } }))}
                          className="bg-[#171717] border-[#262626] font-mono"
                        />
                      </div>
                    )}
                  </div>
                );
              })()
            ) : cur.selected.game === "riftbound" ? (
              /* Riftbound tiene 4 variantes: normal / promo / overnumbered / alt-art.
                 A diferencia de Pokémon (que solo muestra el selector cuando el API
                 confirma Reverse Holo por carta), en Riftbound siempre ofrecemos
                 las 4 porque cualquier carta puede tener print especial. */
              <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-3 items-center -mb-2 p-2 bg-[#EF4444]/5 border border-[#EF4444]/20 rounded">
                <Label className="text-neutral-300 text-xs uppercase tracking-wider whitespace-nowrap">Variante</Label>
                <Select value={cur.form.variant} onValueChange={(v) => setCur(d => ({ ...d, form: { ...d.form, variant: v } }))}>
                  <SelectTrigger data-testid="publish-variant" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#171717] border-[#262626] text-white">
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="promo">Promo</SelectItem>
                    <SelectItem value="overnumbered">Overnumbered (★)</SelectItem>
                    <SelectItem value="alt_art">Alterna (Alt-Art)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : cur.selected.variants_available && cur.selected.variants_available.reverse === true ? (
              /* La carta trae versión Reverse Holo según TCGdex → ofrecemos
                 el selector. Solo aparece cuando el API lo confirma para
                 esta carta específica. */
              <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-3 items-center -mb-2 p-2 bg-amber-500/5 border border-amber-500/20 rounded">
                <Label className="text-neutral-300 text-xs uppercase tracking-wider whitespace-nowrap">Variante</Label>
                <Select value={cur.form.variant} onValueChange={(v) => setCur(d => ({ ...d, form: { ...d.form, variant: v } }))}>
                  <SelectTrigger data-testid="publish-variant" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#171717] border-[#262626] text-white">
                    <SelectItem value="normal">{isHoloCard(cur.selected) ? "Holo" : "Normal"}</SelectItem>
                    <SelectItem value="reverse_holo">Reverse Holo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div className="text-[11px] text-neutral-500 -mb-2">
                Esta carta no tiene versión Reverse Holo según el API. Si tienes una versión especial no listada, créala como carta nueva desde el buscador.
              </div>
            )}
            {(cur.selected.first_edition_available || cur.form.first_edition) && (
              <label className="flex items-start gap-2 p-3 rounded border border-amber-500/40 bg-amber-500/5 cursor-pointer" data-testid="publish-first-edition">
                <input type="checkbox" checked={!!cur.form.first_edition} data-testid="publish-first-edition-checkbox" className="mt-0.5 accent-[#EF4444]"
                  onChange={(e) => setCur((d) => ({ ...d, form: { ...d.form, first_edition: e.target.checked } }))} />
                <span className="text-xs text-amber-100"><b>1.ª edición</b> — la publicación pasa a revisión del admin antes de publicarse.</span>
              </label>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Idioma de la copia</Label>
                <Select value={cur.form.language} onValueChange={(v) => { setCur(d => ({ ...d, form: { ...d.form, language: v } })); if (cur.selected) loadReference(cur.selected.id, v, cur.form.variant); }}>
                  <SelectTrigger data-testid="publish-language" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#171717] border-[#262626] text-white">
                    <SelectItem value="es">Español</SelectItem>
                    <SelectItem value="en">Inglés</SelectItem>
                    <SelectItem value="ja">Japonés</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                    {(cur.selected.is_sealed || (cur.selected.product_type && cur.selected.product_type !== "card")) && <SelectItem value="na">No aplica</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              {!(cur.selected.is_sealed || (cur.selected.product_type && cur.selected.product_type !== "card")) && (
              <div>
                <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Condición</Label>
                <Select value={cur.form.condition} onValueChange={(v) => setCur(d => ({ ...d, form: { ...d.form, condition: v } }))}>
                  <SelectTrigger data-testid="publish-condition" className="bg-[#171717] border-[#262626]"><SelectValue>{condShort(cur.form.condition)}</SelectValue></SelectTrigger>
                  <SelectContent className="bg-[#171717] border-[#262626] text-white max-w-[min(100vw-2rem,var(--radix-select-trigger-width))]" data-testid="publish-condition-list">
                    {conditionOptions(isLotProduct(cur.selected) || cur.form.condition === "MIXED").map((o) => (
                      <SelectItem key={o.value} value={o.value} className="whitespace-normal items-start">
                        <span className="block">{condShort(o.value)}</span>
                        <span className="block text-[11px] text-neutral-400 leading-snug">{condDesc(o.value)}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="mt-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded text-[11px] text-amber-300" data-testid="condition-warning">
                  ⚠ Declara honestamente. Si un comprador demuestra en disputa que el estado real no coincide con lo declarado, recibes un <b>strike automático</b>.
                </div>
              </div>
              )}
              <div>
                <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Precio USD (lo que paga el comprador)</Label>
                <Input data-testid="publish-price" type="number" min="0.25" step="0.01" value={cur.form.price} onChange={(e) => setCur(d => ({ ...d, form: { ...d.form, price: e.target.value } }))} onBlur={(e) => { const v = parseFloat(e.target.value); if (!isNaN(v) && v >= 0.25 && !scrolledToPhotosRef.current) { scrolledToPhotosRef.current = true; smoothScrollTo(photosRef); } }} required className="bg-[#171717] border-[#262626]" />
                <div className="mt-1 text-[10px] text-neutral-500">Precio mínimo por carta: $0.25.</div>
                {netPrice > 0 && (
                  <div className="mt-2 p-3 bg-[#0A0A0A] border border-[#262626] rounded-[14px] text-sm" data-testid="publish-net-breakdown">
                    <SellerNetCards price={netPrice} quantity={cur.form.quantity} ship={hasShipRates(shippingConfig) ? shipRange(shippingConfig) : null}
                      onConfigureShip={() => setShippingModalOpen(true)} />
                    {tiers.length > 0 && (() => {
                      // Explicación desplegable — TODOS los números salen del config.
                      // $0.10 = tiers[0].min_fee ; $3 = tiers[0].max ; ejemplo calculado con sellerNetFor.
                      const firstTier = tiers[0];
                      const minFeeVal = Number(firstTier.min_fee ?? 0);
                      const firstTierMax = firstTier.max;
                      // Precio de ejemplo dinámico — regla: SIEMPRE dejar al vendedor con un
                      // neto claramente positivo y entendible, sea cual sea la config.
                      //
                      // Fórmula: partir de 4× el min_fee (garantiza neto ≥ 3× min_fee ≈ 75% del
                      // precio cuando el mínimo aplica) o del piso del input ($0.25), redondeado
                      // hacia arriba a incrementos de $0.05 para que el número quede limpio.
                      // Cinturón + tirantes: si por alguna config extrema el neto quedara ≤ 0,
                      // subimos el precio hasta que sea positivo (invariante inviolable).
                      const roundUpNickel = (v) => Math.ceil(v * 20) / 20;
                      let examplePrice = roundUpNickel(Math.max(0.25, minFeeVal * 4));
                      let exampleBreakdown = sellerNetFor(examplePrice, tiers);
                      let exampleCommission = exampleBreakdown.hittcg;
                      let exampleNet = Number((examplePrice - exampleCommission).toFixed(2));
                      let guard = 0;
                      while (exampleNet <= 0 && guard < 30) {
                        examplePrice = roundUpNickel(examplePrice + Math.max(0.25, minFeeVal));
                        exampleBreakdown = sellerNetFor(examplePrice, tiers);
                        exampleCommission = exampleBreakdown.hittcg;
                        exampleNet = Number((examplePrice - exampleCommission).toFixed(2));
                        guard++;
                      }
                      const hasThreshold = typeof firstTierMax === "number" && firstTierMax > 0;
                      return (
                        <details className="mt-3 pt-3 border-t border-[#262626] group" data-testid="publish-how-it-works">
                          <summary className="flex items-center justify-between cursor-pointer list-none text-[11px] text-neutral-300 hover:text-white transition-colors">
                            <span className="font-medium">¿Cómo se calcula lo que recibo?</span>
                            <ChevronDown size={14} className="text-neutral-500 transition-transform group-open:rotate-180" />
                          </summary>
                          <div className="mt-3 space-y-2 text-[11px] text-neutral-400 leading-relaxed" data-testid="publish-how-it-works-body">
                            <p>
                              De cada venta, HitTCG cobra una comisión pequeña que cubre el pago seguro y mantener la plataforma. Es el <b className="text-neutral-200">porcentaje del precio</b>, o un <b className="text-neutral-200">mínimo por carta</b> — lo que sea mayor.
                            </p>
                            <p>
                              En cartas baratas el porcentaje es apenas unos centavos, así que se aplica el mínimo.
                              {hasThreshold ? (
                                <> Por eso, en las cartas de menos de <b className="text-neutral-200">${Number(firstTierMax).toFixed(0)}</b>, dejamos ese mínimo bien bajo (<b className="text-neutral-200">${minFeeVal.toFixed(2)}</b>) a propósito: queremos que subir cartas económicas también te valga la pena.</>
                              ) : (
                                <> Por eso dejamos ese mínimo bien bajo (<b className="text-neutral-200">${minFeeVal.toFixed(2)}</b>) a propósito: queremos que subir cartas económicas también te valga la pena.</>
                              )}
                            </p>
                            <div className="p-2 bg-[#111] border border-[#262626] rounded text-neutral-300" data-testid="publish-how-it-works-example">
                              <span className="text-neutral-500">Ejemplo:</span> una carta de <b>${examplePrice.toFixed(2)}</b> → comisión <b>${exampleCommission.toFixed(2)}</b> → te quedas con <b className="text-emerald-400">${exampleNet.toFixed(2)}</b>.
                            </div>
                            <p className="text-neutral-300">
                              Y lo más importante: <b>cuando tu carta se venda, tu ganancia queda fija</b>. Nadie te la baja después.
                            </p>
                          </div>
                        </details>
                      );
                    })()}
                    {cur.reference && <div className="mt-2 pt-2 border-t border-[#262626] text-xs text-amber-300">💡 Mercado: ~${cur.reference.avg.toFixed(2)} promedio / ${cur.reference.median.toFixed(2)} mediana</div>}
                    {!cur.reference && <div className="mt-2 pt-2 border-t border-[#262626] text-xs text-neutral-500">Aún no hay ventas suficientes para precio de referencia.</div>}
                  </div>
                )}
              </div>
              <div>
                <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Cantidad (todas con la misma condición)</Label>
                <Input
                  data-testid="publish-quantity"
                  type="number"
                  inputMode="numeric"
                  min="1"
                  step="1"
                  value={cur.form.quantity ?? ""}
                  onFocus={(e) => e.target.select()}
                  onChange={(e) => {
                    // Allow empty during editing so the user can clear the field.
                    // The submit path coerces empty/invalid values to 1 at validation time.
                    const raw = e.target.value;
                    if (raw === "") {
                      setCur(d => ({ ...d, form: { ...d.form, quantity: "" } }));
                      return;
                    }
                    const n = Math.max(1, parseInt(raw, 10) || 1);
                    setCur(d => ({ ...d, form: { ...d.form, quantity: n, bulk_disclaimer_accepted: n > 1 ? d.form.bulk_disclaimer_accepted : false } }));
                  }}
                  onBlur={(e) => {
                    if (e.target.value === "" || parseInt(e.target.value, 10) < 1) {
                      setCur(d => ({ ...d, form: { ...d.form, quantity: 1 } }));
                    }
                  }}
                  required
                  className="bg-[#171717] border-[#262626]"
                />
                {parseInt(cur.form.quantity) > 1 && (
                  <label className="mt-3 flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/40 rounded cursor-pointer" data-testid="bulk-disclaimer">
                    <input
                      type="checkbox"
                      data-testid="bulk-disclaimer-checkbox"
                      checked={!!cur.form.bulk_disclaimer_accepted}
                      onChange={(e) => setCur(d => ({ ...d, form: { ...d.form, bulk_disclaimer_accepted: e.target.checked } }))}
                      className="mt-0.5 accent-[#EF4444]"
                    />
                    <span className="text-[11px] text-amber-200 leading-relaxed">
                      Declaro que <b>TODAS las {cur.form.quantity} unidades</b> de esta publicación están en la condición <b>{cur.form.condition}</b>. Si alguna llega en condición distinta a la declarada, el comprador puede abrir una disputa y recibiré un strike. Si tengo la misma carta en distintas condiciones, crearé publicaciones separadas.
                    </span>
                  </label>
                )}
              </div>
              <div>
                <ShippingConfigPreview shippingConfig={shippingConfig} sellerProvince={user?.province} onEdit={() => setShippingModalOpen(true)} />
                <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Envío (USD) — fallback para esta carta</Label>
                <Input
                  data-testid="publish-ship-cost"
                  type="number"
                  min="0"
                  step="0.5"
                  value={cur.form.ship_cost}
                  onChange={(e) => setCur(d => ({ ...d, form: { ...d.form, ship_cost: e.target.value, ship_cost_from_config: false } }))}
                  className="bg-[#171717] border-[#262626]"
                />
                {cur.form.ship_cost_from_config && shippingConfig && (
                  <div className="mt-1.5 text-[10px] text-emerald-400" data-testid="ship-cost-inherited">
                    ✓ Heredado de tus tarifas configuradas ({shippingConfig.mode === "simple" ? "modo simple" : shippingConfig.mode === "detailed" ? "por provincia" : "solo encuentro"}). Puedes cambiarlo para esta carta si quieres.
                  </div>
                )}
              </div>
              <div>
                <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Envío gratis desde N artículos (opcional)</Label>
                <Input data-testid="publish-free-ship" type="number" min="1" value={cur.form.free_ship_min_items} onChange={(e) => setCur(d => ({ ...d, form: { ...d.form, free_ship_min_items: e.target.value } }))} placeholder="Ej: 5" className="bg-[#171717] border-[#262626]" />
                <div className="mt-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded text-[11px] text-emerald-200 leading-relaxed" data-testid="free-ship-hint">
                  <b>¿Qué es esto?</b> Escribe el <b>número mínimo de artículos</b> que un comprador debe llevarte en un <b>mismo pedido</b> para que <b>NO le cobres envío</b>.<br />
                  <b>Ejemplo:</b> si pones <b>5</b>, cualquier comprador que te compre <b>5 o más artículos</b> a la vez tendrá envío gratis.<br />
                  Si lo dejas <b>vacío</b>, no se aplica este modo. Aplica solo a <b>artículos tuyos</b> — no mezcla con otros vendedores.
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Envío gratis desde $X de compra (opcional)</Label>
                <Input data-testid="publish-free-ship-amount" type="number" min="1" step="0.5" value={cur.form.free_ship_min_amount} onChange={(e) => setCur(d => ({ ...d, form: { ...d.form, free_ship_min_amount: e.target.value } }))} placeholder="Ej: 30" className="bg-[#171717] border-[#262626]" />
                <div className="mt-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded text-[11px] text-emerald-200 leading-relaxed" data-testid="free-ship-amount-hint">
                  <b>¿Qué es esto?</b> Escribe el <b>monto mínimo en dólares</b> que un comprador debe gastarte en un mismo pedido para que <b>NO le cobres envío</b>.<br />
                  <b>Ejemplo:</b> si pones <b>30</b>, quien te compre <b>$30 o más</b> (sumado) tendrá envío gratis.<br />
                  Puedes usar los dos modos: <b>si el comprador cumple cualquiera de los dos</b> (cantidad de artículos <b>O</b> monto), el envío es gratis. Si dejas los dos vacíos, siempre cobrarás envío.
                </div>
              </div>
            </div>

            <div className="p-4 bg-purple-500/5 border border-purple-500/30 rounded-md space-y-3" data-testid="graded-section">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="publish-graded-checkbox"
                  checked={!!cur.form.graded}
                  onChange={(e) => setCur(d => ({ ...d, form: { ...d.form, graded: e.target.checked } }))}
                  className="mt-0.5 accent-purple-400"
                />
                <span className="text-sm">
                  <b className="text-purple-200">Esta carta está GRADEADA</b>
                  <div className="text-[11px] text-purple-100/70 mt-0.5">Calificada por una casa profesional (PSA, BGS, CGC, SGC). Las gradeadas van a revisión del admin para validar la certificación.</div>
                </span>
              </label>
              {cur.form.graded && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pl-6" data-testid="graded-fields">
                  <div>
                    <Label className="text-neutral-400 mb-1.5 block text-xs uppercase tracking-wider">Casa gradeadora</Label>
                    <Select value={cur.form.graded_house} onValueChange={(v) => setCur(d => ({ ...d, form: { ...d.form, graded_house: v } }))}>
                      <SelectTrigger data-testid="publish-graded-house" className="bg-[#171717] border-[#262626]"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#171717] border-[#262626] text-white">
                        <SelectItem value="PSA">PSA</SelectItem>
                        <SelectItem value="BGS">BGS (Beckett)</SelectItem>
                        <SelectItem value="CGC">CGC</SelectItem>
                        <SelectItem value="SGC">SGC</SelectItem>
                        <SelectItem value="OTHER">Otra</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-neutral-400 mb-1.5 block text-xs uppercase tracking-wider">Grado / calificación</Label>
                    <Input
                      data-testid="publish-graded-grade"
                      value={cur.form.graded_grade}
                      onChange={(e) => setCur(d => ({ ...d, form: { ...d.form, graded_grade: e.target.value } }))}
                      placeholder="Ej: 10, 9.5, 8"
                      required={cur.form.graded}
                      className="bg-[#171717] border-[#262626] font-mono"
                    />
                  </div>
                  <div className="sm:col-span-2 text-[11px] text-purple-100/70">
                    Sube fotos claras donde se lea el número de certificación y la calificación en el slab. El admin valida antes de que salga al catálogo.
                  </div>
                </div>
              )}
            </div>

            <div ref={photosRef} {...photoDrop.dropProps} className={photoDrop.dragging ? "ring-2 ring-[#FF4438] rounded-md" : ""} data-testid="publish-photos-drop">
              <Label className="text-neutral-400 mb-2 block text-xs uppercase tracking-wider">Fotos reales (mínimo {MIN_PHOTOS} · recomendado {REC_PHOTOS}+)</Label>
              {wide && <div className="text-[11px] text-neutral-500 mb-2">Arrastra las fotos aquí o pégalas (Ctrl+V)</div>}
              <MultiPhotoInput
                photos={cur.photos}
                onChange={(next) => setCur(d => ({ ...d, photos: next }))}
                min={MIN_PHOTOS}
                recommended={REC_PHOTOS}
                max={12}
                testid="publish-photos"
              />
              <div className="mt-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-200">
                <b>Fotos:</b> con {MIN_PHOTOS} (frente y reverso) ya puedes publicar. Recomendamos {REC_PHOTOS}+ (esquinas y detalles) para dar más confianza al comprador y respaldo ante disputas. <b>SIN filtros ni sombras fuertes.</b>
              </div>
            </div>
          </>
        )}

        {cur.selected && (
          <Button type="button" variant="outline" onClick={addToQueue} disabled={loading} data-testid="publish-add-to-queue"
            className="w-full h-11 rounded-full border-[#333] bg-transparent text-white hover:bg-white/5">
            + Añadir a la cola y buscar otra{wide && <span className="ml-2 text-[10px] text-neutral-500">ENTER</span>}
          </Button>
        )}
        <Button data-testid="publish-submit" type="submit" disabled={loading} className="w-full bg-[#EF4444] hover:bg-[#DC2626] h-12">
          {loading ? (
            <span className="inline-flex items-center gap-2">
              <HitRing tone="light" size={20} />
              {progress.step || "Publicando…"}
            </span>
          ) : (
            `Publicar ${(() => {
             // Type + quantity aware label: "Publicar 4 productos sellados" /
             // "Publicar 1 carta" / "Publicar 2 cartas y 1 producto sellado".
             const totals = drafts.filter((d) => d.selected).reduce((acc, d) => {
               const q = Math.max(1, parseInt(d.form?.quantity, 10) || 1);
               const isSealed = d.selected?.is_sealed || (d.selected?.product_type && d.selected.product_type !== "card");
               if (isSealed) acc.sealed += q; else acc.card += q;
               return acc;
             }, { card: 0, sealed: 0 });
             const parts = [];
             if (totals.card)   parts.push(`${totals.card} ${totals.card === 1 ? "carta" : "cartas"}`);
             if (totals.sealed) parts.push(`${totals.sealed} ${totals.sealed === 1 ? "producto sellado" : "productos sellados"}`);
             return parts.length ? parts.join(" y ") : `${drafts.length} ${drafts.length === 1 ? "artículo" : "artículos"}`;
           })()}`
          )}
        </Button>
      </form>
      )}
      {mode === "cargar" && (
        <div className={wide ? "col-start-3 row-start-1 row-span-4" : ""}>
          <QueueSheet drafts={drafts} activeTab={activeTab} minPhotos={MIN_PHOTOS} isComplete={isComplete} inline={wide}
            onOpen={openQueueItem} onRemove={removeTab} onPublish={() => submitAll(null, true)} loading={loading}
            onRetry={(i) => submitAll(null, true, [drafts[i]].filter(isComplete))} />
        </div>
      )}
      </div>

      {/* Modal de escáner con IA — solo aplica a cartas individuales.
          Al confirmar, invoca `pickCard` con el objeto normalizado y setea
          la búsqueda para dejar historial visible al usuario. */}
      <CardScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        game={activeGame}
        onCardDetected={(card, meta) => {
          setResults([card]);
          setSearched(true);
          // CardScanner detectó un idioma vía OCR: aplicarlo si difiere
          // del actual del formulario, sin importar el default global.
          // (Antes había un check hardcodeado contra "es" — quedaba roto
          // cuando el default cambió a "en"; ahora es simétrico.)
          if (meta?.language) {
            setCur(d => (d.form.language !== meta.language
              ? { ...d, form: { ...d.form, language: meta.language } }
              : d
            ));
          }
          pickCard(card);
        }}
      />

      {loading && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 pt-[max(1rem,env(safe-area-inset-top))] pb-[max(1rem,env(safe-area-inset-bottom))]" data-testid="publish-progress-overlay">
          <div className="bg-[#111] border border-[#262626] rounded-md p-6 max-w-md w-full space-y-4">
            <div className="flex items-center gap-3">
              <HitRing tone="light" size={22} />
              <div className="font-display text-lg">{progress.step || "Procesando…"}</div>
            </div>
            {progress.sub && (
              <div className="text-sm text-neutral-400" data-testid="publish-progress-sub">{progress.sub}</div>
            )}
            {progress.total > 0 && (
              <div>
                <div className="flex justify-between text-xs text-neutral-500 mb-1">
                  <span>Progreso</span>
                  <span>{Math.min(progress.current + 1, progress.total)} / {progress.total}</span>
                </div>
                <div className="h-2 bg-[#0A0A0A] rounded overflow-hidden border border-[#262626]">
                  <div
                    className="h-full bg-[#EF4444] transition-all duration-300"
                    style={{ width: `${Math.min(100, Math.round(((progress.current) / progress.total) * 100))}%` }}
                  />
                </div>
              </div>
            )}
            <div className="text-[11px] text-neutral-500">
              No cierres la ventana. Optimizamos tus fotos automáticamente para que suban rápido.
            </div>
          </div>
        </div>
      )}
    </div>
    {/* Modal de edición de tarifas de envío. Abre el mismo ShippingForm del
        SellerDashboard dentro de un Dialog para que el vendedor pueda editar
        sin perder el borrador de la publicación actual. */}
    <Dialog open={shippingModalOpen} onOpenChange={setShippingModalOpen}>
      <DialogContent className="max-w-2xl bg-[#0A0A0A] border-[#262626] max-h-[90vh] overflow-y-auto" data-testid="publish-shipping-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Editar tarifas de envío</DialogTitle>
        </DialogHeader>
        <ShippingForm
          currentConfig={shippingConfig}
          sellerProvince={user?.province}
          onSaved={async () => {
            try {
              const r = await api.get("/seller/shipping-config");
              setShippingConfig(r.data || null);
              setShippingModalOpen(false);
              toast.success("Tarifas actualizadas — se reflejan en tu próxima publicación");
            } catch { /* toast already shown by form */ }
          }}
        />
      </DialogContent>
    </Dialog>
  </>);
}

// -------------------------------------------------------------------------
// Feb 2026 v30.4 — Yu-Gi-Oh: combobox buscable para rareza+set.
// Reemplaza al Select plano cuando la carta tiene N printings (ej. 59 en Dark
// Magician). El filtro de cmdk opera sobre el prop `value` de cada
// `CommandItem`, que concatenamos con rareza+set_code+set_name normalizados
// (sin acentos, lowercase) → cumple el requisito del founder:
// "secret" filtra Secret Rare, "LOB" filtra el set LOB, "legend of blue"
// filtra por nombre. Si no hay matches, mostramos texto claro.
// -------------------------------------------------------------------------
function _ygoNorm(s) {
  return String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, ""); // strip combining diacritics
}

function YugiohPrintingCombobox({ printings, value, onChange }) {
  const [open, setOpen] = useState(false);
  // Mapa key → printing para resolver el label de lo seleccionado al vuelo.
  const byKey = useMemo(() => {
    const m = new Map();
    for (const p of printings) {
      const k = `${p.rarity || ""}|${p.set_code || ""}`;
      if (!m.has(k)) m.set(k, p);
    }
    return m;
  }, [printings]);
  const selected = value ? byKey.get(value) : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="publish-yugioh-printing"
          className="w-full justify-between bg-[#171717] border-[#262626] hover:bg-[#1f1f1f] text-left font-normal h-auto py-2 min-h-10"
        >
          {selected ? (
            <span className="flex-1 truncate text-sm">
              <span className="font-semibold text-white">{selected.rarity}</span>
              <span className="text-neutral-400"> · {selected.set_name}</span>
              <span className="text-neutral-500 font-mono"> [{selected.set_code}]</span>
            </span>
          ) : (
            <span className="text-neutral-500 text-sm">{`Elige una de las ${printings.length} impresiones reales`}</span>
          )}
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-0 bg-[#171717] border-[#262626]"
        align="start"
      >
        <Command
          className="bg-[#171717] text-white"
          // Filtro case-insensitive + acento-tolerante. cmdk pasa (value, search)
          // y espera un score (0 = no match). Nuestro `value` ya viene
          // normalizado desde `CommandItem.value` abajo.
          filter={(itemValue, search) => {
            const needle = _ygoNorm(search).trim();
            if (!needle) return 1;
            return itemValue.includes(needle) ? 1 : 0;
          }}
        >
          <CommandInput
            data-testid="publish-yugioh-printing-search"
            placeholder="Busca por rareza, set o código (ej. 'secret', 'LOB', 'legend of blue')"
            className="h-9 text-sm"
          />
          <CommandList className="max-h-[320px]">
            <CommandEmpty data-testid="publish-yugioh-printing-empty" className="py-6 text-center text-sm text-neutral-400">
              Sin resultados, revisa el término.
            </CommandEmpty>
            <CommandGroup>
              {printings.map((p, i) => {
                const key = `${p.rarity || ""}|${p.set_code || ""}`;
                // Haystack: rareza + set_code + set_name todos normalizados.
                // cmdk comparará `search` contra esto gracias al prop `filter`
                // custom que definimos arriba.
                const haystack = _ygoNorm(
                  `${p.rarity || ""} ${p.set_code || ""} ${p.set_name || ""}`
                );
                return (
                  <CommandItem
                    key={`${key}-${i}`}
                    value={haystack}
                    onSelect={() => {
                      onChange(key);
                      setOpen(false);
                    }}
                    data-testid={`publish-yugioh-printing-item-${i}`}
                    className="cursor-pointer data-[selected=true]:bg-purple-500/20"
                  >
                    <span className="font-semibold">{p.rarity || "(sin rareza)"}</span>
                    <span className="text-neutral-400"> · {p.set_name || "(sin set)"}</span>
                    <span className="text-neutral-500 font-mono ml-auto text-[11px]">[{p.set_code || "?"}]</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
